import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Activity, Search, Filter, Calendar, Download, Clock } from 'lucide-react';
import api from '../utils/api';
import { formatDate, formatTime, getStatusLabel } from '../utils/helpers';

const ReportsPage = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('appointments');
  
  // Appointments Report State
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [aptFilters, setAptFilters] = useState({
    startDate: '',
    endDate: '',
    service: '',
    status: ''
  });
  const [aptSearch, setAptSearch] = useState('');
  const [loadingApt, setLoadingApt] = useState(false);

  // Activity Log State
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [actFilters, setActFilters] = useState({
    startDate: '',
    endDate: '',
    userId: ''
  });
  const [actSearch, setActSearch] = useState('');
  const [loadingAct, setLoadingAct] = useState(false);

  // Exposure Time Report State
  const [exposureData, setExposureData] = useState([]);
  const [expFilters, setExpFilters] = useState({
    startDate: '',
    endDate: ''
  });
  const [loadingExp, setLoadingExp] = useState(false);

  useEffect(() => {
    fetchServices();
    fetchUsers();
    fetchAppointments();
    fetchActivities();
    fetchExposureData();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await api.get('/services');
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchAppointments = async () => {
    setLoadingApt(true);
    try {
      const response = await api.get('/appointments');
      setAppointments(response.data);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoadingApt(false);
    }
  };

  const fetchActivities = async () => {
    setLoadingAct(true);
    try {
      const response = await api.get('/reports/activity-logs');
      // Sort by timestamp descending (latest first)
      const sorted = response.data.sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      );
      setActivities(sorted);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoadingAct(false);
    }
  };

  const fetchExposureData = async () => {
    setLoadingExp(true);
    try {
      const params = new URLSearchParams();
      if (expFilters.startDate) params.append('start_date', expFilters.startDate);
      if (expFilters.endDate) params.append('end_date', expFilters.endDate);
      
      const response = await api.get(`/reports/exposure-time?${params.toString()}`);
      setExposureData(response.data);
    } catch (error) {
      console.error('Error fetching exposure data:', error);
    } finally {
      setLoadingExp(false);
    }
  };

  // Filter and search appointments
  const filteredAppointments = appointments.filter(apt => {
    // Date filter
    if (aptFilters.startDate && apt.scheduled_date < aptFilters.startDate) return false;
    if (aptFilters.endDate && apt.scheduled_date > aptFilters.endDate) return false;
    
    // Service filter
    if (aptFilters.service && apt.service_id !== aptFilters.service) return false;
    
    // Status filter
    if (aptFilters.status && apt.status !== aptFilters.status) return false;
    
    // Search
    if (aptSearch) {
      const search = aptSearch.toLowerCase();
      return (
        apt.patient_name?.toLowerCase().includes(search) ||
        apt.patient_phone?.includes(search) ||
        apt.service_name?.toLowerCase().includes(search)
      );
    }
    
    return true;
  });

  // Filter and search activities
  const filteredActivities = activities.filter(act => {
    // Date filter
    if (actFilters.startDate) {
      const actDate = new Date(act.timestamp).toISOString().split('T')[0];
      if (actDate < actFilters.startDate) return false;
    }
    if (actFilters.endDate) {
      const actDate = new Date(act.timestamp).toISOString().split('T')[0];
      if (actDate > actFilters.endDate) return false;
    }
    
    // User filter
    if (actFilters.userId && act.user_id !== actFilters.userId) return false;
    
    // Search
    if (actSearch) {
      const search = actSearch.toLowerCase();
      return (
        act.action_type?.toLowerCase().includes(search) ||
        act.description?.toLowerCase().includes(search) ||
        act.user_name?.toLowerCase().includes(search)
      );
    }
    
    return true;
  });

  const exportToCSV = (data, filename) => {
    // Simple CSV export - you can enhance this
    const headers = Object.keys(data[0] || {}).join(',');
    const rows = data.map(item => Object.values(item).join(','));
    const csv = [headers, ...rows].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Reports & Analytics</h1>
          <p className="text-gray-600">Comprehensive reports and activity tracking</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('appointments')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'appointments'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Appointments Report
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'activity'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Activity className="w-4 h-4 inline mr-2" />
            Activity Log
          </button>
          <button
            onClick={() => setActiveTab('exposure')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'exposure'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Clock className="w-4 h-4 inline mr-2" />
            Patient Exposure Time
          </button>
        </div>

        {/* Appointments Report Tab */}
        {activeTab === 'appointments' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Filter className="w-5 h-5 text-purple-600" />
                <h3 className="font-semibold text-gray-900">Filters & Search</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={aptFilters.startDate}
                    onChange={(e) => setAptFilters({...aptFilters, startDate: e.target.value})}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={aptFilters.endDate}
                    onChange={(e) => setAptFilters({...aptFilters, endDate: e.target.value})}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Service</Label>
                  <select
                    value={aptFilters.service}
                    onChange={(e) => setAptFilters({...aptFilters, service: e.target.value})}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">All Services</option>
                    {services.map(service => (
                      <option key={service.id} value={service.id}>{service.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label>Status</Label>
                  <select
                    value={aptFilters.status}
                    onChange={(e) => setAptFilters({...aptFilters, status: e.target.value})}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">All Statuses</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="checked_in">Checked In</option>
                    <option value="checked_out">Checked Out</option>
                    <option value="rescheduled">Rescheduled</option>
                    <option value="forwarded">Forwarded</option>
                    <option value="no_show">No Show</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-2">
                <div className="flex-1">
                  <Label>Search</Label>
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search by name, phone, or service..."
                      value={aptSearch}
                      onChange={(e) => setAptSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    onClick={() => {
                      setAptFilters({startDate: '', endDate: '', service: '', status: ''});
                      setAptSearch('');
                    }}
                    variant="outline"
                  >
                    Clear
                  </Button>
                  <Button
                    onClick={() => exportToCSV(filteredAppointments, 'appointments-report.csv')}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>
            </div>

            {/* Results Summary */}
            <div className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div>
                <p className="text-sm text-gray-600">Total Results</p>
                <p className="text-2xl font-bold text-purple-600">{filteredAppointments.length}</p>
              </div>
              {loadingApt && <p className="text-sm text-gray-600">Loading...</p>}
            </div>

            {/* Appointments Table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Time</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Patient</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Service</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Token</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredAppointments.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          No appointments found matching your criteria
                        </td>
                      </tr>
                    ) : (
                      filteredAppointments.map(apt => (
                        <tr key={apt.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">{formatDate(apt.scheduled_date)}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {apt.scheduled_time ? formatTime(apt.scheduled_time) : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{apt.patient_name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{apt.patient_phone}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{apt.service_name}</td>
                          <td className="px-4 py-3 text-sm font-mono text-purple-600">
                            {apt.token_number || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <Badge className="text-xs">{getStatusLabel(apt.status)}</Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Activity Log Tab */}
        {activeTab === 'activity' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Filter className="w-5 h-5 text-purple-600" />
                <h3 className="font-semibold text-gray-900">Filters & Search</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={actFilters.startDate}
                    onChange={(e) => setActFilters({...actFilters, startDate: e.target.value})}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={actFilters.endDate}
                    onChange={(e) => setActFilters({...actFilters, endDate: e.target.value})}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>User</Label>
                  <select
                    value={actFilters.userId}
                    onChange={(e) => setActFilters({...actFilters, userId: e.target.value})}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">All Users</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.name} ({user.role})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-2">
                <div className="flex-1">
                  <Label>Search</Label>
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search by action, service, patient name..."
                      value={actSearch}
                      onChange={(e) => setActSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    onClick={() => {
                      setActFilters({startDate: '', endDate: '', userId: ''});
                      setActSearch('');
                    }}
                    variant="outline"
                  >
                    Clear
                  </Button>
                  <Button
                    onClick={() => exportToCSV(filteredActivities, 'activity-log.csv')}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </div>
            </div>

            {/* Results Summary */}
            <div className="flex items-center justify-between bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div>
                <p className="text-sm text-gray-600">Total Activities</p>
                <p className="text-2xl font-bold text-purple-600">{filteredActivities.length}</p>
              </div>
              {loadingAct && <p className="text-sm text-gray-600">Loading...</p>}
            </div>

            {/* Activity Log Table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Timestamp</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Action</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Details</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">PIN</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredActivities.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                          No activities found matching your criteria
                        </td>
                      </tr>
                    ) : (
                      filteredActivities.map((activity, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {new Date(activity.timestamp).toLocaleString('en-IN', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true,
                              timeZone: 'Asia/Kolkata'
                            })}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{activity.user_name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            <Badge variant="outline" className="text-xs">{activity.user_role}</Badge>
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-purple-600">
                            {activity.action_type?.replace(/_/g, ' ').toUpperCase()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 max-w-md">
                            {activity.description}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {activity.pin_user_name || (activity.user_pin ? 'PIN User' : '-')}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Exposure Time Report Tab */}
        {activeTab === 'exposure' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Filter className="w-5 h-5 text-purple-600" />
                <h3 className="font-semibold text-gray-900">Date Range Filter</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={expFilters.startDate}
                    onChange={(e) => setExpFilters({...expFilters, startDate: e.target.value})}
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={expFilters.endDate}
                    onChange={(e) => setExpFilters({...expFilters, endDate: e.target.value})}
                    className="mt-1"
                  />
                </div>

                <div className="flex items-end gap-2">
                  <Button
                    onClick={() => {
                      setExpFilters({startDate: '', endDate: ''});
                      fetchExposureData();
                    }}
                    variant="outline"
                  >
                    Clear
                  </Button>
                  <Button
                    onClick={fetchExposureData}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <Filter className="w-4 h-4 mr-2" />
                    Apply Filters
                  </Button>
                </div>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                <div className="flex items-center gap-3 mb-2">
                  <Clock className="w-6 h-6 text-purple-600" />
                  <h3 className="font-semibold text-gray-900">Total Services</h3>
                </div>
                <p className="text-3xl font-bold text-purple-600">{exposureData.length}</p>
              </div>
              
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="w-6 h-6 text-blue-600" />
                  <h3 className="font-semibold text-gray-900">Total Appointments</h3>
                </div>
                <p className="text-3xl font-bold text-blue-600">
                  {exposureData.reduce((sum, s) => sum + s.total_appointments, 0)}
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
                <div className="flex items-center gap-3 mb-2">
                  <Clock className="w-6 h-6 text-amber-600" />
                  <h3 className="font-semibold text-gray-900">Avg Exposure Time</h3>
                </div>
                <p className="text-3xl font-bold text-amber-600">
                  {exposureData.length > 0
                    ? Math.round(
                        exposureData.reduce((sum, s) => sum + s.average_exposure_minutes, 0) / exposureData.length
                      )
                    : 0}{' '}
                  min
                </p>
              </div>
            </div>

            {/* Results */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">
                  Exposure Time by Service ({exposureData.length} services)
                </h3>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-purple-600 border-purple-300 hover:bg-purple-50"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
              {loadingExp && <p className="text-sm text-gray-600">Loading...</p>}
            </div>

            {/* Service Cards */}
            <div className="grid grid-cols-1 gap-4">
              {exposureData.length === 0 && !loadingExp ? (
                <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                  <Clock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No exposure time data available</p>
                  <p className="text-sm text-gray-400 mt-2">Appointments must be checked out to calculate exposure time</p>
                </div>
              ) : (
                exposureData.map((service) => (
                  <div key={service.service_id} className="bg-white rounded-xl border border-gray-200 p-6 hover:border-purple-300 transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-gray-900">{service.service_name}</h4>
                        <Badge variant="outline" className="mt-2 text-xs">
                          {service.service_type === 'queue_based' ? 'Queue-Based' : 'Time-Based'}
                        </Badge>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-500">Total Appointments</div>
                        <div className="text-2xl font-bold text-purple-600">{service.total_appointments}</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mt-4">
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <div className="text-xs text-blue-600 font-semibold mb-1">Average Exposure</div>
                        <div className="text-xl font-bold text-blue-700">{service.average_exposure_minutes} min</div>
                      </div>
                      
                      <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                        <div className="text-xs text-green-600 font-semibold mb-1">Minimum Time</div>
                        <div className="text-xl font-bold text-green-700">{service.min_exposure_minutes} min</div>
                      </div>
                      
                      <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                        <div className="text-xs text-red-600 font-semibold mb-1">Maximum Time</div>
                        <div className="text-xl font-bold text-red-700">{service.max_exposure_minutes} min</div>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm text-gray-600">
                        <strong>Insight:</strong> Patients spend an average of{' '}
                        <span className="font-bold text-purple-600">{service.average_exposure_minutes} minutes</span> from check-in to check-out for this service.
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ReportsPage;
